/**
 * 성취기준(교육과정) API 클라이언트 — CONTRACT §3·§4
 *
 * - 인증 불필요, `getApiUrl` 사용. 서버가 `Cache-Control: public, max-age=86400` + ETag를 주므로
 *   브라우저 HTTP 캐시가 1차 캐시다. 여기서는 메모리 + localStorage에 사본을 두고
 *   503·네트워크 실패 때 폴백으로만 쓴다(catalog만 24h TTL로 읽기 우선).
 * - localStorage 키: `curriculum:<curriculum>:<dataset_version>:<endpoint>:<params>`
 *   + 슬롯별 최신 버전 포인터 `curriculum:<curriculum>:latest:<endpoint>:<params>`.
 *   같은 교육과정의 새 dataset_version이 오면 그 교육과정의 이전 버전만 지운다.
 * - search/validate 결과는 메모리에만 둔다(쿼리마다 localStorage가 늘어나지 않게).
 * - 모든 함수는 throw하지 않고 `{ ok, data }` 또는 `{ ok: false, error: { code, message, status } }`를 돌려준다.
 */

import { normalizeCurriculumId } from '../config/curricula';
import { getApiUrl } from '../utils/apiConfig';

const CATALOG_TTL_MS = 24 * 60 * 60 * 1000;
const STORAGE_PREFIX = 'curriculum:';
const LATEST_VERSION_TAG = 'latest';
const memoryCache = new Map();

export const CURRICULUM_ERROR_CODE = {
  NETWORK: 'network',
  UNAVAILABLE: 'unavailable',
  UNKNOWN: 'unknown',
};

// 라우터 미배포(404)·게이트웨이 오류·데이터 미적재(503) 모두 사용자에게는 "아직 준비 중"이다.
const UNAVAILABLE_STATUSES = new Set([404, 501, 502, 503, 504]);

const UNAVAILABLE_MESSAGE = '성취기준을 아직 준비 중이에요. 잠시 후 다시 시도해주세요.';
export const CURRICULUM_2015_UNAVAILABLE_MESSAGE = '2015 개정 데이터를 준비 중입니다';
export const CURRICULUM_SPECIAL_UNAVAILABLE_MESSAGE = '특수교육 기본 교육과정 데이터를 준비 중입니다';
const UNAVAILABLE_MESSAGES = {
  2015: CURRICULUM_2015_UNAVAILABLE_MESSAGE,
  'special-2022': CURRICULUM_SPECIAL_UNAVAILABLE_MESSAGE,
};
const NETWORK_MESSAGE = '연결을 확인한 뒤 다시 시도해주세요.';
const UNKNOWN_MESSAGE = '성취기준을 불러오지 못했어요. 다시 시도해주세요.';

// 'all'은 전체 검색(교육과정 미지정) 캐시 슬롯.
const normalizeCurriculum = (curriculum) => {
  if (curriculum === 'all') return 'all';
  return normalizeCurriculumId(curriculum);
};

const buildCacheKey = (curriculum, datasetVersion, endpoint, params) => (
  `${STORAGE_PREFIX}${normalizeCurriculum(curriculum)}:${datasetVersion}:${endpoint}:${params}`
);

const buildLatestKey = (curriculum, endpoint, params) => (
  buildCacheKey(curriculum, LATEST_VERSION_TAG, endpoint, params)
);

const memorySlot = (curriculum, endpoint, params) => (
  `${normalizeCurriculum(curriculum)}:${endpoint}:${params}`
);

const hasStorage = () => {
  try {
    return typeof localStorage !== 'undefined' && localStorage !== null;
  } catch {
    return false;
  }
};

const readStorage = (key) => {
  if (!hasStorage()) return null;
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeStorage = (key, value) => {
  if (!hasStorage()) return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // 용량 초과·프라이빗 모드: 캐시는 최선 노력, 흐름을 막지 않는다
  }
};

const removeStorage = (key) => {
  if (!hasStorage()) return;
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

/** 해당 교육과정의 현재 dataset_version이 아닌 항목만 지운다. 다른 교육과정 캐시는 유지한다. */
const pruneOtherVersions = (currentVersion, curriculum = '2022') => {
  if (!hasStorage() || !currentVersion) return;
  const prefix = `${STORAGE_PREFIX}${normalizeCurriculum(curriculum)}:`;
  try {
    const stale = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (!key || !key.startsWith(prefix)) continue;
      const version = key.slice(prefix.length).split(':')[0];
      if (version === LATEST_VERSION_TAG) {
        if (readStorage(key)?.dataset_version !== currentVersion) stale.push(key);
      } else if (version !== currentVersion) {
        stale.push(key);
      }
    }
    stale.forEach(removeStorage);
  } catch {
    // ignore
  }
};

const readCached = (endpoint, params, { allowStale = true, curriculum = '2022', ttlMs } = {}) => {
  const mem = memoryCache.get(memorySlot(curriculum, endpoint, params));
  if (mem?.data) {
    const isStale = Boolean(ttlMs) && Date.now() - mem.cachedAt > ttlMs;
    if (!isStale || allowStale) {
      return { data: mem.data, stale: isStale };
    }
  }

  const latest = readStorage(buildLatestKey(curriculum, endpoint, params));
  if (!latest?.dataset_version) return null;
  const stored = readStorage(buildCacheKey(curriculum, latest.dataset_version, endpoint, params));
  if (!stored?.data) return null;
  const cachedAt = stored.cached_at || latest.cached_at || 0;
  const isStale = Boolean(ttlMs) && Date.now() - cachedAt > ttlMs;
  if (isStale && !allowStale) return null;
  memoryCache.set(memorySlot(curriculum, endpoint, params), { cachedAt, data: stored.data });
  return { data: stored.data, stale: isStale };
};

const writeCached = (endpoint, params, data, { curriculum = '2022', persist = true } = {}) => {
  const datasetVersion = data?.dataset_version || 'unknown';
  const cachedAt = Date.now();
  memoryCache.set(memorySlot(curriculum, endpoint, params), { cachedAt, data });
  if (!persist) return;

  const previous = readStorage(buildLatestKey(curriculum, endpoint, params));
  if (previous?.dataset_version && previous.dataset_version !== datasetVersion) {
    removeStorage(buildCacheKey(curriculum, previous.dataset_version, endpoint, params));
  }
  writeStorage(buildCacheKey(curriculum, datasetVersion, endpoint, params), {
    cached_at: cachedAt,
    data,
  });
  writeStorage(buildLatestKey(curriculum, endpoint, params), {
    cached_at: cachedAt,
    dataset_version: datasetVersion,
  });
};

export const buildCurriculumError = (status, message, { curriculum } = {}) => {
  if (UNAVAILABLE_STATUSES.has(status)) {
    return {
      error: {
        code: CURRICULUM_ERROR_CODE.UNAVAILABLE,
        message: UNAVAILABLE_MESSAGES[curriculum] || UNAVAILABLE_MESSAGE,
        status,
      },
      ok: false,
    };
  }
  if (status === 0 || status === undefined || status === null) {
    return {
      error: {
        code: CURRICULUM_ERROR_CODE.NETWORK,
        message: message || NETWORK_MESSAGE,
        status: 0,
      },
      ok: false,
    };
  }
  return {
    error: {
      code: CURRICULUM_ERROR_CODE.UNKNOWN,
      message: message || UNKNOWN_MESSAGE,
      status,
    },
    ok: false,
  };
};

const fallbackOrError = (endpoint, params, status, curriculum) => {
  const cached = readCached(endpoint, params, { allowStale: true, curriculum });
  if (cached?.data) {
    return { data: cached.data, fromCache: true, ok: true };
  }
  return buildCurriculumError(status, undefined, { curriculum });
};

const requestJson = async (path, {
  body,
  curriculum = '2022',
  endpoint,
  method = 'GET',
  params,
  persist = true,
} = {}) => {
  let response;
  try {
    response = await fetch(getApiUrl(path), {
      body: body === undefined ? undefined : JSON.stringify(body),
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      method,
    });
  } catch {
    return fallbackOrError(endpoint, params, 0, curriculum);
  }
  if (!response.ok) {
    return fallbackOrError(endpoint, params, response.status, curriculum);
  }
  let data;
  try {
    data = await response.json();
  } catch {
    // 프록시 HTML 등 JSON이 아닌 응답
    return fallbackOrError(endpoint, params, response.status === 200 ? 500 : response.status, curriculum);
  }
  writeCached(endpoint, params, data, { curriculum, persist });
  return { data, ok: true };
};

const toQuery = (entries) => entries
  .filter(([, value]) => value !== undefined && value !== null && value !== '')
  .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
  .join('&');

export const fetchCurriculumCatalog = async ({ curriculum = '2022' } = {}) => {
  const endpoint = 'catalog';
  const resolved = normalizeCurriculum(curriculum);
  const params = toQuery([['curriculum', resolved]]);
  const fresh = readCached(endpoint, params, {
    allowStale: false,
    curriculum: resolved,
    ttlMs: CATALOG_TTL_MS,
  });
  if (fresh?.data) {
    return { data: fresh.data, fromCache: true, ok: true };
  }
  const result = await requestJson(`/api/curriculum/standards/catalog?${params}`, {
    curriculum: resolved,
    endpoint,
    params,
  });
  if (result.ok && !result.fromCache) {
    pruneOtherVersions(result.data?.dataset_version, resolved);
  }
  return result;
};

export const fetchSubjectStandards = async (
  subjectId,
  { curriculum = '2022', includeCommentary = false } = {},
) => {
  const endpoint = 'standards';
  const resolved = normalizeCurriculum(curriculum);
  const params = toQuery([
    ['curriculum', resolved],
    ['include', includeCommentary ? 'commentary' : ''],
    ['subject_id', subjectId],
  ]);
  return requestJson(`/api/curriculum/standards?${params}`, {
    curriculum: resolved,
    endpoint,
    params,
  });
};

/**
 * 계약 v1.4(사용자 피드백 9/10 14:38): `curriculum`·`schoolLevel`을 생략하면 서버가 2022+2015 × 초·중·고·전문교과
 * 전체를 한 번에 검색한다. `preferSchoolLevel`/`preferSubjectId`는 필터가 아닌 정렬 힌트로, 현재 맥락의 결과를
 * 위로 올린다(limit에 잘려도 현재 학교급·과목이 남게). 결과 항목에는 `curriculum`이 들어온다.
 * 전체 검색 결과는 교육과정을 가리지 않으므로 캐시 슬롯은 `all`이다.
 */
export const GLOBAL_SEARCH_SLOT = 'all';

export const searchStandards = async (q, {
  curriculum,
  limit = 50,
  preferCurriculum,
  preferSchoolLevel,
  preferSubjectId,
  schoolLevel,
  subjectId,
} = {}) => {
  const endpoint = 'search';
  const resolved = curriculum ? normalizeCurriculum(curriculum) : undefined;
  const params = toQuery([
    ['curriculum', resolved],
    ['limit', limit],
    // 정렬 힌트: 선택기가 보고 있는 교육과정. 같은 코드가 2022·2015에 다 있을 때 이 쪽이 위로 온다(필터 아님).
    ['prefer_curriculum', preferCurriculum ? normalizeCurriculum(preferCurriculum) : undefined],
    ['prefer_school_level', preferSchoolLevel],
    ['prefer_subject_id', preferSubjectId],
    ['q', q],
    ['school_level', schoolLevel],
    ['subject_id', subjectId],
  ]);
  return requestJson(`/api/curriculum/standards/search?${params}`, {
    curriculum: resolved || GLOBAL_SEARCH_SLOT,
    endpoint,
    params,
    persist: false,
  });
};

/**
 * 계약 v1.3: 응답 `invalid[]` 항목은 `{ input, reason, candidates? }`이며 `reason: "ambiguous-code"`일 때
 * `candidates`(Record 요약 배열)가 붙는다. 응답은 가공 없이 그대로 돌려준다. 프론트는 `keys`만 보낸다(codes는 전 샤드 로드).
 */
export const validateStandards = async ({ codes, curriculum = '2022', keys, refs } = {}) => {
  const endpoint = 'validate';
  const resolved = normalizeCurriculum(curriculum);
  if (Array.isArray(refs) && refs.length > 0) {
    const params = toQuery([[
      'refs',
      refs
        .map((ref) => `${normalizeCurriculum(ref?.curriculum)}:${ref?.key || ref?.code || ''}`)
        .sort()
        .join(','),
    ]]);
    return requestJson('/api/curriculum/standards/validate', {
      body: { refs },
      curriculum: resolved,
      endpoint,
      method: 'POST',
      params,
      persist: false,
    });
  }
  const params = toQuery([
    ['codes', (codes || []).slice().sort().join(',')],
    ['curriculum', resolved],
    ['keys', (keys || []).slice().sort().join(',')],
  ]);
  return requestJson(`/api/curriculum/standards/validate?curriculum=${encodeURIComponent(resolved)}`, {
    body: { codes, keys },
    curriculum: resolved,
    endpoint,
    method: 'POST',
    params,
    persist: false,
  });
};

export const __resetCurriculumApiCache = () => {
  memoryCache.clear();
};
